import React from 'react';
import { Tag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { categoryKey } from '../../content/catalog';

type Props = {
  total: number;
  // Category list is derived from the data (ordered by count desc) — not
  // hardcoded — so new DB categories appear without a frontend change.
  categories: string[];
  categoryCounts: Record<string, number>;
  regions: string[];
  activeCategory: string | null;
  activeRegion: string | null;
  onCategory: (c: string | null) => void;
  onRegion: (r: string | null) => void;
};

const FilterSidebar: React.FC<Props> = ({
  total,
  categories,
  categoryCounts,
  regions,
  activeCategory,
  activeRegion,
  onCategory,
  onRegion,
}) => {
  const { t } = useTranslation();
  return (
  <aside className="space-y-8">
    <div className="space-y-2">
      <p className="font-heading font-bold text-xs tracking-[0.2em] text-warm-500 uppercase mb-3">
        {t('filter_category')}
      </p>
      <button
        onClick={() => onCategory(null)}
        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl font-heading font-bold text-sm transition-colors ${
          !activeCategory
            ? 'bg-brand-secondary text-brand-primary'
            : 'text-warm-700 hover:bg-warm-100'
        }`}
      >
        <span>{t('filter_all_experiences')}</span>
        <span className="text-xs opacity-70">{total}</span>
      </button>
      {categories.map((cat) => {
        const active = activeCategory === cat;
        return (
          <button
            key={cat}
            onClick={() => onCategory(active ? null : cat)}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm transition-colors ${
              active
                ? 'bg-warm-100 text-warm-800 font-heading font-bold'
                : 'text-warm-600 hover:bg-warm-100'
            }`}
          >
            <span className="flex items-center gap-3">
              <Tag className="w-4 h-4" />
              {t(categoryKey(cat))}
            </span>
            <span className="text-xs text-warm-500">
              {categoryCounts[cat] ?? 0}
            </span>
          </button>
        );
      })}
    </div>

    <div className="space-y-3">
      <p className="font-heading font-bold text-xs tracking-[0.2em] text-warm-500 uppercase">
        {t('filter_region')}
      </p>
      <div className="flex flex-wrap gap-2">
        {regions.map((region) => {
          const active = activeRegion === region;
          return (
            <button
              key={region}
              onClick={() => onRegion(active ? null : region)}
              className={`px-3 py-1.5 rounded-lg text-sm font-heading font-bold transition-colors ${
                active
                  ? 'bg-brand-accent text-white'
                  : 'bg-warm-100 text-warm-700 hover:bg-warm-200'
              }`}
            >
              {region}
            </button>
          );
        })}
      </div>
    </div>
  </aside>
  );
};

export default FilterSidebar;
